import React, { useEffect, useMemo, useState } from 'react';
import Button from '@cashew/button';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import { ModalConfirm } from '../../../components';
import {
  ChargebackFormType,
  ChargebackPayment,
  ChargebackProps,
  ResultType,
} from './index.types';
import ChargebackForm from './ChargebackForm';
import { formatEligiblePayments } from './index.functions';

const { api } = require('@cashew/utils');

export default ({ currency, isModalOpen, closeModal }: ChargebackProps) => {
  const { t } = useTranslation();
  const params: any = useParams();
  const { token } = useSelector((state: any) => state.user);

  const [showResult, setShowResult] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [eligiblePayments, setEligiblePayments] = useState<ChargebackPayment[]>(
    []
  );
  const [resultCode, setResultCode] = useState<ResultType>('none');
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [formDetails, setFormDetails] = useState<ChargebackFormType>({
    requestDate: moment().format('YYYY-MM-DD'),
  });

  const formattedEligiblePayments = useMemo(
    () => formatEligiblePayments({ payments: eligiblePayments, currency }),
    [eligiblePayments, currency]
  );

  const isDisabled = useMemo(
    () =>
      !formDetails?.paymentId ||
      !formDetails?.requestDate ||
      !formDetails?.authCode?.trim() ||
      !formDetails?.arn?.trim() ||
      !formDetails?.requestDate?.trim() ||
      formDetails?.requestDate === 'Invalid date',
    [formDetails]
  );

  const resultDescription = useMemo(
    () => (
      <div className="resultDescriptionStatus" style={{ marginTop: 16 }}>
        {t(`chargeback.result.description.${resultCode}`)}
      </div>
    ),
    [resultCode]
  );

  const modalDescription = useMemo(
    () =>
      isConfirmationModalOpen
        ? 'Are you sure you want to log this chargeback?'
        : 'Choose the related payment and enter the request date, Auth code & ARN',
    [isConfirmationModalOpen]
  );

  useEffect(() => {
    api
      .get({
        endpoint: `payments/chargeback/${params.orderId}/eligibility/payments`,
        headers: {
          Authorization: token,
        },
      })
      .then((resp: any) => {
        setEligiblePayments(resp.data);
      })
      .catch(() => {
        setEligiblePayments([]);
      });
  }, []);

  const handleFormDetailsUpdate = ({
    key,
    value,
  }: {
    key: string;
    value: string;
  }) => {
    setFormDetails((prevState) => ({ ...prevState, [key]: value }));
  };

  const chargebackForm = useMemo(
    () => (
      <ChargebackForm
        formDetails={formDetails}
        setFormDetails={handleFormDetailsUpdate}
        payments={formattedEligiblePayments}
      />
    ),
    [formDetails, handleFormDetailsUpdate, formattedEligiblePayments]
  );

  const onRefund = () => {
    const chosenPayment = eligiblePayments?.find(
      ({ paymentId }) => paymentId?.toString() === formDetails?.paymentId
    );
    const data = {
      ...formDetails,
      paymentProvider: chosenPayment?.paymentProvider,
    };
    setIsSubmitting(true);
    api
      .post({
        endpoint: 'payments/chargeback',
        params: data,
      })
      .then(() => {
        setResultCode('success');
      })
      .catch((error: any) => {
        setResultCode(error?.response?.data?.errorCode);
      })
      .finally(() => {
        setShowResult(true);
        setIsSubmitting(false);
      });
  };

  const confirmButtons = (
    <div style={{ display: 'flex' }}>
      <Button
        extraClass="btn-black secondary"
        onClick={() => {
          if (isConfirmationModalOpen) {
            setIsConfirmationModalOpen(false);
          } else {
            setResultCode('warning');
            setShowResult(true);
          }
        }}
      >
        {isConfirmationModalOpen ? 'No' : 'Cancel'}
      </Button>
      <Button
        extraClass="order-details__confirm"
        disabled={isDisabled || isSubmitting}
        loading={isSubmitting}
        onClick={() =>
          isConfirmationModalOpen
            ? onRefund()
            : setIsConfirmationModalOpen(true)
        }
      >
        {isConfirmationModalOpen ? 'Yes' : t('button.confirm')}
      </Button>
    </div>
  );

  const resultButtons = (
    <div>
      <Button
        extraClass="btn-blue"
        onClick={() => {
          closeModal(false);
        }}
      >
        Ok
      </Button>
    </div>
  );

  return (
    <ModalConfirm
      isVisible={isModalOpen}
      onClose={() => {
        closeModal(false);
      }}
      showCloseButton={false}
      confirmTitle="Log chargeback request"
      confirmDescription={modalDescription}
      confirmForm={isConfirmationModalOpen ? null : chargebackForm}
      confirmButtons={confirmButtons}
      resultTitle={t(`chargeback.result.title.${resultCode}`)}
      resultDescriptionComponent={resultDescription}
      resultButtons={resultButtons}
      showResult={showResult}
      resultStatus={
        ['none', 'success', 'warning'].includes(resultCode)
          ? resultCode
          : 'error'
      }
    />
  );
};
